import { Roi, LabelingService, ChangeProjectionService, RoiChange, CounterDeltas, MosaicMapper } from '@analysis/domain';
import { type RoiChangesRepository, type RoiRepository } from '../../ports';

export interface UpdateRoiPositionPayload {
  roi: Roi;
  originalRoi: Roi;
}

export interface UpdateRoiPositionResult {
  deletedRoi: Roi;
  createdRoi: Roi;
  changes: Map<string, RoiChange | null>;
  counterDeltas: CounterDeltas;
  removedOriginalId?: string;
}

export class UpdateRoiPositionUseCase {
  constructor(private readonly roiChanges: RoiChangesRepository, private readonly rois: RoiRepository) {}

  public async execute(payload: UpdateRoiPositionPayload): Promise<UpdateRoiPositionResult> {
    const { roi, originalRoi } = payload;

    const previousChanges = await this.roiChanges.getByIds([originalRoi.id]);

    const changes: Map<string, RoiChange | null> = new Map();

    const previousChange = previousChanges.get(originalRoi.id);
    const isNewOriginal = previousChange?.isNew ?? false;

    let hydratedOriginal: Roi = originalRoi;
    if (!originalRoi.additionalLabelsIncluded && originalRoi.uuid) {
      if (previousChange?.isNew) {
        hydratedOriginal = previousChange.snapshot ?? originalRoi;
      } else {
        const baselines = await this.rois.batchGetByIds([originalRoi.uuid]);
        const baseline = baselines.get(originalRoi.uuid);
        if (baseline) {
          hydratedOriginal = previousChange
            ? ChangeProjectionService.applyChanges(baseline, previousChange)
            : baseline;
        }
      }
    }

    const currentRoi = previousChange
      ? ChangeProjectionService.applyChanges(hydratedOriginal, previousChange)
      : hydratedOriginal;

    const labelKeys = Array.from(currentRoi.labels.keys());
    const resultDeletion = LabelingService.removeAllLabels(currentRoi);

    const newRoiId = MosaicMapper.createSpatialKey({
      ...roi.coordinates,
      findingId: originalRoi.findingId,
    });

    const updatedRoi: Roi = {
      ...originalRoi,
      coordinates: roi.coordinates,
      id: newRoiId,
      labels: currentRoi.labels,
    };

    if (isNewOriginal) {
      changes.set(originalRoi.id, null);
    } else {
      const deletionChange: RoiChange = {
        id: originalRoi.id,
        sampleId: originalRoi.sampleId,
        assetId: originalRoi.assetId,
        creatorId: originalRoi.creatorId,
        timestamp: Date.now(),
        labelsAdded: resultDeletion.changes.labelsAdded,
        labelsRemoved: resultDeletion.changes.labelsRemoved,
        isNew: false,
        snapshot: previousChange?.snapshot,
      };
      changes.set(originalRoi.id, deletionChange);
    }

    const creationChange: RoiChange = {
      id: newRoiId,
      sampleId: updatedRoi.sampleId,
      assetId: updatedRoi.assetId,
      creatorId: updatedRoi.creatorId,
      timestamp: Date.now(),
      labelsAdded: labelKeys,
      labelsRemoved: [],
      isNew: true,
      snapshot: updatedRoi,
    };

    changes.set(newRoiId, creationChange);

    await this.roiChanges.upsertRoiChanges(changes);

    const oldRoiLabels = Array.from(currentRoi.labels.keys());
    const newRoiLabels = Array.from(updatedRoi.labels.keys());

    const deletionDelta = ChangeProjectionService.computeCounterDeltas(oldRoiLabels, [], [], oldRoiLabels);
    const creationDelta = ChangeProjectionService.computeCounterDeltas([], newRoiLabels, newRoiLabels, []);
    const counterDeltas = ChangeProjectionService.mergeCounterDeltas([deletionDelta, creationDelta]);

    return {
      deletedRoi: resultDeletion.roi,
      createdRoi: updatedRoi,
      changes,
      counterDeltas,
      removedOriginalId: isNewOriginal ? originalRoi.id : undefined,
    };
  }
}
